import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Info, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { generarConsolidado, listarConsolidados } from '../../services/controlProduccionConsolidados.services';
import {
  MESES, buildDailyInputs, computeControlDiario, resolveParams,
} from '../../helpers/controlDiarioCalculations';
import {
  COLORS, GROUPS, COLUMNS, KPI_BAND, NF, CONCILIACION_STYLE,
  SUNDAY_FONT, EMPTY_DAY_FILL, AC_LOW_FILL, AC_HIGH_FILL,
} from '../../helpers/controlDiarioLayout';

const hex = (h) => (h ? `#${h}` : undefined);
// Ancho de columna de Excel (caracteres) -> píxeles
const px = (w) => Math.round(w * 7 + 5);

// Formatea un valor con el código de formato de Excel de la columna
const fmt = (value, nf) => {
  if (value === null || value === undefined || value === '') return '';
  if (nf === NF.date) {
    const d = value instanceof Date ? value : new Date(value);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
  if (typeof value !== 'number') return String(value);
  const num = (n, dec) => Number(n).toLocaleString('es-CO', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  switch (nf) {
    case NF.int: return num(value, 0);
    case NF.one: return num(value, 1);
    case NF.two: return num(value, 2);
    case '0': return num(value, 0);
    case NF.pct: return `${num(value * 100, 1)}%`;
    case NF.diff: return value < 0 ? `(${num(Math.abs(value), 0)})` : num(value, 0);
    default: return String(value);
  }
};

const borderStyle = `1px solid ${hex(COLORS.border)}`;
const STICKY_B_LEFT = px(COLUMNS[0].width);

// Reporte "Control diario de producción": la hoja del mes del Excel del cliente, con los
// datos del sistema en las celdas amarillas y las mismas fórmulas en el resto.
const ControlDiarioContent = ({ controlDiario, laundry, year, month }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Consolidado guardado de este mes (si existe), para mostrar "Guardar" o "Recalcular"
  const [consolidado, setConsolidado] = useState(null);
  const laundryId = laundry?._id;

  const cargarConsolidado = useCallback(async () => {
    if (!laundryId || !year || !month) return;
    try {
      const list = await listarConsolidados({ laundryId, year });
      setConsolidado(list.find((c) => c.month === month) || null);
    } catch (e) {
      console.error('Error consultando el consolidado del mes:', e);
    }
  }, [laundryId, year, month]);

  useEffect(() => { setConsolidado(null); cargarConsolidado(); }, [cargarConsolidado]);

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const generadoPor = { id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined };
      await generarConsolidado({ laundryIds: [laundryId], year, month, generadoPor });
      toast({ title: consolidado ? 'Consolidado recalculado' : 'Consolidado guardado', description: `${MESES[month - 1]} ${year} — ${laundry?.nombre || ''}` });
      await cargarConsolidado();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  const { data, loading, error } = controlDiario;
  const params = useMemo(() => resolveParams(laundry), [laundry]);

  const result = useMemo(() => {
    if (!data) return null;
    const inputs = buildDailyInputs({ year, month, ...data, supervisor: laundry?.supervisor || '' });
    return computeControlDiario(inputs, params);
  }, [data, year, month, laundry, params]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { downloadControlDiarioExcel } = await import('../../helpers/controlDiarioExcel');
      await downloadControlDiarioExcel({ laundryNombre: laundry?.nombre || '', year, month, result, params });
    } catch (e) {
      console.error('Error generando el Excel:', e);
      toast({ title: 'Error', description: 'No se pudo generar el archivo de Excel.', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-xl p-4">{error}</div>;
  }
  if (loading || !result) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Armando el control diario de producción…
      </div>
    );
  }

  const cellStyle = (c, row) => {
    const g = GROUPS[c.group];
    const style = {
      border: borderStyle,
      color: hex(g.font),
      background: hex(g.fill) || '#FFFFFF',
      textAlign: c.align || 'center',
      whiteSpace: c.wrap ? 'normal' : 'nowrap',
      minWidth: px(c.width),
      maxWidth: c.wrap ? px(c.width) : undefined,
    };
    if (!row.date) style.background = hex(EMPTY_DAY_FILL);
    if ((c.col === 'A' || c.col === 'B') && row.dia === 'Dom') {
      style.color = hex(SUNDAY_FONT);
      style.fontWeight = 700;
    }
    if (c.col === 'AG' && CONCILIACION_STYLE[row.AG]) {
      const s = CONCILIACION_STYLE[row.AG];
      style.background = hex(s.fill);
      style.color = hex(s.font);
      style.fontWeight = s.bold ? 700 : 400;
    }
    if (c.col === 'AC' && typeof row.AC === 'number') {
      if (row.AC < -params.tolVar) style.background = hex(AC_LOW_FILL);
      else if (row.AC > params.tolVar) style.background = hex(AC_HIGH_FILL);
    }
    if (c.col === 'A' || c.col === 'B') {
      style.position = 'sticky';
      style.left = c.col === 'A' ? 0 : STICKY_B_LEFT;
      style.zIndex = 1;
    }
    return style;
  };

  const cellValue = (c, row) => {
    if (c.col === 'A') return fmt(row.date, NF.date);
    if (c.col === 'B') return row.dia || '';
    return fmt(row[c.col], c.nf);
  };

  const headerCell = (c) => ({
    border: borderStyle,
    background: hex(GROUPS[c.group].header),
    color: '#FFFFFF',
    fontWeight: 700,
    whiteSpace: 'pre-line',
    textAlign: 'center',
    minWidth: px(c.width),
    position: 'sticky',
    top: 0,
    zIndex: c.col === 'A' || c.col === 'B' ? 3 : 2,
    ...(c.col === 'A' || c.col === 'B' ? { left: c.col === 'A' ? 0 : STICKY_B_LEFT } : {}),
  });

  const footerCell = (c, kind) => ({
    border: borderStyle,
    background: kind === 'total' ? hex(COLORS.navy) : hex(COLORS.kpiValue),
    color: kind === 'total' ? '#FFFFFF' : hex(COLORS.navy),
    fontWeight: 700,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    ...(c.col === 'A' || c.col === 'B' ? { position: 'sticky', left: c.col === 'A' ? 0 : STICKY_B_LEFT, zIndex: 1 } : {}),
  });

  const groupSpans = [
    { span: 2, text: '', color: null },
    { span: 9, text: '◄ DATOS A DILIGENCIAR (celdas amarillas) ►', color: COLORS.pink },
    { span: 2, text: '◄ CÁLCULOS AUTOMÁTICOS ►', color: COLORS.blue },
    { span: 16, text: '◄ PRODUCTIVIDAD POR HORA AUTORIZADA ►', color: COLORS.coral },
    { span: 4, text: '◄ CONCILIACIÓN FACTURACIÓN ►', color: COLORS.amber },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm p-5 space-y-4" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {/* Título (filas 1–2 del Excel) */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[20px] font-bold" style={{ color: hex(COLORS.navy) }}>REGISTRO DIARIO DE PRODUCCIÓN — LAVANDERÍA</div>
          <div className="text-[13px] font-bold mt-1" style={{ color: hex(COLORS.pink) }}>
            Cliente: {laundry?.nombre || '—'}   |   Mes: {MESES[month - 1]}   |   Año: {year}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={handleGuardar} disabled={saving || !laundryId}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : consolidado ? <RefreshCw className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
              {saving ? 'Guardando…' : consolidado ? 'Recalcular consolidado' : 'Guardar consolidado del mes'}
            </Button>
            <Button onClick={handleDownload} disabled={downloading} className="bg-green-600 hover:bg-green-700 text-white">
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {downloading ? 'Generando…' : 'Descargar Excel'}
            </Button>
          </div>
          {consolidado && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              Consolidado {consolidado.enCurso ? 'en curso' : 'guardado'} el {new Date(consolidado.updatedAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
            </div>
          )}
        </div>
      </div>

      {/* Banda de KPIs (filas 4–6) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2">
        {KPI_BAND.map((k) => (
          <div key={k.key} style={{ border: borderStyle }}>
            <div className="text-[10px] font-bold text-white text-center px-1 py-1 leading-tight" style={{ background: hex(COLORS.kpiLabel) }}>{k.label}</div>
            <div className="text-[18px] font-bold text-center py-2" style={{ background: hex(COLORS.kpiValue), color: hex(COLORS.navy) }}>
              {fmt(result.kpis[k.key], k.nf) || '0'}
            </div>
          </div>
        ))}
      </div>

      {/* Hoja del mes (filas 7–41), con encabezados y Fecha/Día inmovilizados */}
      <div className="overflow-auto max-h-[70vh] border border-gray-200 dark:border-gray-700">
        <table className="border-collapse text-[11px]" style={{ color: hex(COLORS.text) }}>
          <thead>
            <tr>
              {groupSpans.map((g, i) => (
                <th key={i} colSpan={g.span} className="text-left font-bold text-[10px] px-1 py-1 bg-white" style={{ color: hex(g.color) }}>{g.text}</th>
              ))}
            </tr>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.col} className="px-1 py-1 text-[11px] leading-tight h-[52px]" style={headerCell(c)}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.day}>
                {COLUMNS.map((c) => (
                  <td key={c.col} className="px-1 py-[3px] tabular-nums" style={cellStyle(c, row)} title={c.wrap ? row[c.col] || '' : undefined}>
                    {cellValue(c, row)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td colSpan={2} className="px-1 py-1" style={footerCell(COLUMNS[0], 'total')}>TOTAL MES</td>
              {COLUMNS.slice(2).map((c) => (
                <td key={c.col} className="px-1 py-1 tabular-nums" style={footerCell(c, 'total')}>
                  {c.totalNf ? fmt(result.totals[c.col], c.totalNf) : ''}
                </td>
              ))}
            </tr>
            <tr>
              <td colSpan={2} className="px-1 py-1" style={footerCell(COLUMNS[0], 'avg')}>PROMEDIO DÍA</td>
              {COLUMNS.slice(2).map((c) => (
                <td key={c.col} className="px-1 py-1 tabular-nums" style={footerCell(c, 'avg')}>
                  {result.averages[c.col] !== undefined ? fmt(result.averages[c.col], NF.one) : ''}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex items-start gap-2 text-[11.5px] text-[#43474F] dark:text-gray-400">
        <Info className="h-4 w-4 flex-none mt-0.5" />
        <p>
          Celdas amarillas: datos del sistema (ciclos sin abortados, recepciones, alistamientos sin eliminados y horas
          autorizadas). Factor de carga {params.raw.factorCarga}% · tolerancia de variación {params.raw.toleranciaVariacion}% ·
          tolerancia del cliente sobre alistado {params.raw.toleranciaClienteAlistado}% (se cambian en Parametrización &gt;
          Centros de lavado &gt; Configurar indicadores).
        </p>
      </div>
    </div>
  );
};

export default ControlDiarioContent;
